'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import Cookies from 'js-cookie'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Countdown from 'react-countdown'
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'

import styles from './PinVerification.module.css'
import { SuccessPinVerification } from './SuccessPinVerification'
import { cn } from '@/lib/utils'
import { appSelectors, useAppStore } from '@/store'
import { apiClient } from '@/config'
import { Button, Input } from '@/components'

const schema = yup.object().shape({
  email: yup.string().email('Invalid email').required('Email is required'),
  pin: yup
    .array()
    .of(
      yup
        .string()
        .length(1, 'Each digit is required')
        .matches(/^\d$/, 'Must be a digit'),
    )
    .length(4, 'PIN must be 4 digits')
    .required(),
})

type PinSchemaType = yup.InferType<typeof schema>

export const PinCodeForm: React.FC = () => {
  const [expire, setExpire] = useState<Date>()
  const [attemptsLeft, setAttemptsLeft] = useState<number>(3)
  const [error, setError] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [successVerification, setSuccessVerification] = useState<boolean>(false)
  const [isParamEmail, setIsParamEmail] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const router = useRouter()

  const t = useTranslations('auth.pinVerification')

  const { verifyEmail } = useAppStore(appSelectors.auth)

  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      pin: ['', '', '', ''],
    },
  })

  const { register, handleSubmit, setValue, watch } = form

  const email = watch('email')
  const pin = watch('pin')

  const refs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ]

  const resetErrors = () => {
    setError('')
    setErrorMessage('')
  }

  const onSubmitInvalid: SubmitErrorHandler<PinSchemaType> = useCallback(
    (error) => {
      console.error('ERROR: ', error)
      if (error?.email) {
        setError('E-mail is required!')
        setErrorMessage('E-mail is required!')
      }
    },
    [],
  )

  const onSubmitValid: SubmitHandler<PinSchemaType> = useCallback(
    async (data) => {
      const pinString = data.pin.join('')
      setLoading(true)
      try {
        const { data } = await apiClient.postVerifyPin({
          email,
          pin: pinString,
        })

        if (data?.accessToken) {
          Cookies.set('access_token', data.accessToken)
          Cookies.set('refresh_token', data.refreshToken)
          setSuccessVerification(true)
        }
        setLoading(false)
      } catch (error) {
        const message =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.response?.data?.message ?? 'unknownError'
        setError(t(`error.${message}`))
        setErrorMessage(t(`error.${message}`))
        setAttemptsLeft((prev) => prev - 1)
      } finally {
        setLoading(false)
      }
    },
    [email, t],
  )

  const submit = useCallback(() => {
    handleSubmit(onSubmitValid, onSubmitInvalid)()
  }, [handleSubmit, onSubmitValid, onSubmitInvalid])

  const validateInput = (
    ev: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const digit = ev.target.value
    const newPin = [...pin]
    newPin[index] = digit
    setValue('pin', newPin, { shouldValidate: true })

    if (index < 3 && digit !== '') {
      refs[index + 1].current?.focus()
    } else if (index === 3 && newPin.every((d) => d !== '')) {
      submit()
    }
  }

  const onResendEmail = useCallback(async () => {
    resetErrors()
    if (!email) {
      setError(t('error.couldNotSendEmail'))
      return
    }

    try {
      setLoading(true)
      const response = await apiClient.postResendPin({
        email,
      })
      if (response?.data?.expiresAt) {
        setExpire(new Date(response.data.expiresAt))
        setMessage(t('success.emailResent'))
        setErrorMessage('')
        setTimeout(() => {
          setMessage('')
          setAttemptsLeft(3)
          setValue('pin', ['', '', '', ''])
        }, 3000)
      }
    } catch (err) {
      console.log(err)
      setError(t('error.couldNotSendEmail'))
      setErrorMessage(t('error.couldNotSendEmail'))
    }
    setLoading(false)
  }, [email, t, setValue])

  const renderPinCountdown = useCallback(
    ({
      minutes,
      seconds,
      completed,
    }: {
      minutes: number
      seconds: number
      completed: boolean
    }) => {
      if (completed) {
        return (
          <span>
            {t('error.expiredSession')}{' '}
            <Link href="#" onClick={onResendEmail}>
              {t('requestNewCode')}
            </Link>
          </span>
        )
      } else {
        return (
          <p>
            {t('error.codeExpiresIn')}
            <span>
              {' '}
              {minutes.toString().padStart(2, '0')}:
              {seconds.toString().padStart(2, '0')}
            </span>
          </p>
        )
      }
    },
    [t, onResendEmail],
  )

  const onCountdownCompleted = useCallback(() => {
    if (successVerification) {
      router.replace('/')
    }
  }, [successVerification, router])

  useEffect(() => {
    if (refs[0] && refs[0].current) {
      refs[0].current.focus()
      refs[0].current.select()
    }

    const urlParams = new URLSearchParams(window?.location?.search || '')
    const paramEmail = urlParams.get('email')
    if (paramEmail) {
      setValue('email', paramEmail)
    } else if (verifyEmail) {
      setValue('email', verifyEmail)
    } else {
      setIsParamEmail(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyEmail])

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        if (attemptsLeft <= 0) {
          setValue('pin', ['x', 'x', 'x', 'x'])
        } else {
          setValue('pin', ['', '', '', ''])
        }

        setError('')
        refs.forEach((ref) => {
          if (ref?.current) ref.current.value = ''
        })
        refs?.[0].current?.focus()
      }, 1000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, attemptsLeft])

  return (
    <div className="w-full max-w-md my-8">
      <div className="flex flex-col gap-3">
        <div className={styles.PinVerification}>
          {successVerification && <SuccessPinVerification />}

          {!successVerification && (
            <React.Fragment>
              <h2 className={styles.PinVerificationHeader}>{t('title')}</h2>
              <p>{t('enterCode')}</p>
              <form onSubmit={submit}>
                {!isParamEmail && (
                  <div className="space-y-2">
                    <Input
                      {...register('email')}
                      name="email"
                      type="email"
                      disabled={loading}
                      placeholder="E-mail"
                      className="rounded-full text-lg placeholder:text-lg py-3 px-6 h-14"
                    />
                  </div>
                )}
                <div>
                  <div className={styles.PinVerification__PIN}>
                    {[0, 1, 2, 3].map((index) => (
                      <input
                        key={index}
                        placeholder={attemptsLeft <= 0 ? 'X' : '0'}
                        {...register(`pin.${index}`, {
                          onChange: (e) => validateInput(e, index),
                        })}
                        disabled={!!error || attemptsLeft <= 0}
                        ref={refs[index]}
                        className="text-white border-b border-white/30 text-7xl text-center w-1/4 outline-none py-3 px-0"
                        maxLength={1}
                      />
                    ))}
                  </div>
                </div>
                <Button
                  disabled={loading}
                  type="submit"
                  className="text-primary text-lg my-6"
                  variant="glow"
                  size="lg"
                >
                  {t('buttonCaption')}
                </Button>
              </form>

              {message && (
                <div className={styles.PinVerification__message}>{message}</div>
              )}
              {errorMessage && (
                <div
                  className={cn(
                    styles.PinVerification__message,
                    styles.PinVerification__message__error,
                  )}
                >
                  {errorMessage}
                </div>
              )}

              {expire && attemptsLeft > 0 && (
                <Countdown
                  onComplete={onCountdownCompleted}
                  renderer={renderPinCountdown}
                  date={expire}
                />
              )}

              <Button
                disabled={loading}
                onClick={onResendEmail}
                variant="outline"
                size="lg"
                className="mt-4"
              >
                {t('requestNewPinButtonCaption')}
              </Button>
            </React.Fragment>
          )}
          {loading && <div className={styles.PinVerification__overlay}></div>}
        </div>
      </div>
    </div>
  )
}
